<template>
  <div class="standard-header">
    <div class="header-content">
      <div class="header-main">
        <div v-if="icon" class="header-icon hidden md:flex">
          <i :class="icon"></i>
        </div>
        <div class="header-text">
          <h1 class="header-title">
            <i v-if="icon" :class="icon + ' mr-2 md:hidden'"></i>
            {{ title }}
          </h1>
          <p v-if="subtitle" class="header-subtitle hidden md:block">{{ subtitle }}</p>
        </div>
      </div>
      <div
        v-if="actions && actions.length > 0"
        class="header-actions"
      >
        <Button
          v-for="(action, index) in actions"
          :key="index"
          :icon="action.icon"
          :label="action.label"
          class="md:inline-flex"
          :size="action.size || 'small'"
          :severity="action.severity || 'secondary'"
          :outlined="action.outlined"
          :class="action.class || ''"
          :disabled="action.disabled"
          @click="action.command"
          v-tooltip="action.tooltip"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import Button from 'primevue/button';

// Props definition
defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: 'pi pi-list'
  },
  actions: {
    type: Array,
    default: () => []
  },
});
</script>

<script>
export default {
  name: 'StandardHeader'
};
</script>

<style scoped>
/* Mobile First - StandardHeader optimized for iPhone SE (375px) */
.standard-header {
  background: linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
}

.header-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  width: 100%;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: linear-gradient(145deg, #4a89dc, #357abd);
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(74, 137, 220, 0.3);
  flex-shrink: 0;
}

.header-icon i {
  font-size: 1.25rem;
  color: white;
}

.header-text {
  flex: 1;
  min-width: 0;
  text-align: center;
  width: 100%;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;

  /* Mobile: centered with icon and proper truncation */
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.header-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.5rem;
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .standard-header {
    padding: 1rem;
  }

  .header-title {
    font-size: 1.5rem;
  }

  .header-actions {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .standard-header {
    padding: 1.25rem;
    margin-bottom: 1.25rem;
  }

  .header-content {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .header-main {
    justify-content: flex-start;
    width: auto;
  }

  .header-text {
    text-align: left;
    width: auto;
  }

  .header-title {
    font-size: 1.75rem;
    text-align: left;
    justify-content: flex-start;
  }

  .header-subtitle {
    max-width: 250px;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
    align-items: center;
    width: auto;
    margin-top: 0;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .standard-header {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .header-title {
    font-size: 1.75rem;
  }

  .header-subtitle {
    max-width: none;
  }

  .header-actions {
    gap: 1rem;
  }
}

/* Focus states for accessibility */
.header-actions :deep(.p-button:focus) {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

/* Hover effects */
.header-icon:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 137, 220, 0.4);
  transition: all 0.2s ease;
}
</style>